import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppInsightsContext, useTrackMetric } from '@microsoft/applicationinsights-react-js';
import { NotFound } from '@loadable-elements/NotFound';
import { DEFAULT_IMG_URL } from '@lib/constants';
import useSelector from '@redux/typedHooks';
import { actions as guides } from '@redux/modules/consumerGuides';
import { LayoutWithMainContent } from '../../../components/layout/Layout';
import { InternalSearchCardType } from '../../../components/layout/InternalSearchMetaData';
import { useWindowLocation } from '../../../hooks/Window/useWindowLocation';
import { TestedProductContent } from './TestedProductContent/TestedProductContent';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { LoaderContent } from '../../LoaderPage';

interface Props {
  guideSlug: string;
  testSlug: string;
}

export const path = 'forbrukertest/:guideSlug/:testSlug';

const TestedProductPage = ({ testSlug, guideSlug }: Props) => {
  const windowPath = useWindowLocation();
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'Testprodukt');
  const dispatch = useDispatch();
  const { simpleToken } = useAuth0Token();
  const { oneProduct, selectedGuide, relatedProductsGuideId, isUpdatingGuide, isUpdatingProducts, errorState } =
    useSelector((state) => state.consumerGuides);
  const { id, title, ingress, image, seoConfig } = oneProduct;
  const seoDescription = seoConfig?.introduction ? seoConfig.introduction : ingress;
  const seoTitle = seoConfig?.title ? seoConfig.title : title;

  useEffect(() => {
    if (testSlug) dispatch(guides.clearTestedProducts());
  }, [dispatch, testSlug]);

  useEffect(() => {
    if (testSlug) dispatch(guides.getOneProduct.request(testSlug));
  }, [dispatch, testSlug, simpleToken]);

  useEffect(() => {
    if (guideSlug) {
      dispatch(guides.clearRelatedProducts());
      dispatch(guides.getOneGuide.request(guideSlug));
    }
  }, [dispatch, guideSlug]);

  // Once per guide. Waits for the product: both requests share `isUpdatingProducts`, so a related
  // response arriving first would end the loader before the product is there.
  const selectedGuideId = selectedGuide?.slug === guideSlug ? selectedGuide.id : undefined;
  useEffect(() => {
    if (selectedGuideId && relatedProductsGuideId !== selectedGuideId && !isUpdatingProducts) {
      dispatch(guides.getRelatedProducts.request(selectedGuideId));
    }
  }, [dispatch, selectedGuideId, relatedProductsGuideId, isUpdatingProducts]);

  if (!testSlug || !guideSlug || errorState) {
    return <NotFound />;
  }

  if (isUpdatingGuide || isUpdatingProducts || !oneProduct) {
    return <LoaderContent />;
  }

  return (
    <LayoutWithMainContent
      title={title}
      description={ingress}
      url={windowPath}
      imgUrl={image?.srcSet?.jpgLarge || DEFAULT_IMG_URL}
      gtmArgs={{
        page_type: 'consumerTestProductPage',
        pageCategory: 'Forbrukertest',
        contentId: id || '',
      }}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      isGuidePage
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Article,
      }}
    >
      <TestedProductContent product={oneProduct} guideSlug={guideSlug} />
    </LayoutWithMainContent>
  );
};

export default TestedProductPage;
